import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, Mail, Shield, ArrowLeft, RefreshCw, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { format } from "date-fns";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [heroCount, setHeroCount] = useState(0);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [leadsRes, countRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/hero-count`)
      ]);
      setLeads(leadsRes.data);
      setHeroCount(countRes.data.count);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#0D0D0D] py-8 px-6" data-testid="admin-dashboard">
      <Toaster position="top-center" />
      
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors font-rajdhani"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Scanner
            </Link>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-rajdhani rounded-sm hover:bg-white/20 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="max-w-6xl mx-auto mb-8">
        <h1 className="font-orbitron text-2xl sm:text-3xl font-bold text-white tracking-wider flex items-center gap-3">
          <Shield className="w-8 h-8 text-neon-purple" />
          Admin Dashboard
        </h1>
        <p className="font-rajdhani text-white/60 mt-2">
          Manage leads and view visitor data
        </p>
      </div>

      {/* Stats cards */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-neon-cyan" />
            <span className="font-rajdhani text-white/60 uppercase text-sm tracking-wider">
              Total Scans
            </span>
          </div>
          <p className="font-orbitron text-3xl text-white">{heroCount}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <Mail className="w-5 h-5 text-neon-purple" />
            <span className="font-rajdhani text-white/60 uppercase text-sm tracking-wider">
              Leads Captured
            </span>
          </div>
          <p className="font-orbitron text-3xl text-white">{leads.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-neon-green" />
            <span className="font-rajdhani text-white/60 uppercase text-sm tracking-wider">
              Conversion Rate
            </span>
          </div>
          <p className="font-orbitron text-3xl text-white">
            {heroCount > 0 ? Math.round((leads.length / heroCount) * 100) : 0}%
          </p>
        </motion.div>
      </div>

      {/* Leads table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="max-w-6xl mx-auto glass rounded-lg overflow-hidden"
      >
        <div className="p-6 border-b border-white/10">
          <h2 className="font-orbitron text-lg text-white tracking-wider">
            Captured Leads
          </h2>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-neon-purple animate-spin mx-auto mb-4" />
            <p className="font-rajdhani text-white/60">Loading leads...</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="font-rajdhani text-white/60">No leads captured yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="font-orbitron text-xs text-white/60">Name</th>
                  <th className="font-orbitron text-xs text-white/60">Email</th>
                  <th className="font-orbitron text-xs text-white/60">Profession</th>
                  <th className="font-orbitron text-xs text-white/60">Superhero Title</th>
                  <th className="font-orbitron text-xs text-white/60">Date</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, index) => (
                  <motion.tr
                    key={lead.id || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`lead-row-${index}`}
                  >
                    <td className="font-rajdhani text-white">
                      {lead.visitor?.name || "Unknown"}
                    </td>
                    <td className="font-rajdhani text-neon-cyan">
                      {lead.email}
                    </td>
                    <td className="font-rajdhani text-white/80">
                      {lead.visitor?.profession || "-"}
                    </td>
                    <td className="font-rajdhani text-neon-purple">
                      {lead.result?.superhero_title || "-"}
                    </td>
                    <td className="font-rajdhani text-white/60">
                      {lead.createdAt ? format(new Date(lead.createdAt), "MMM d, yyyy") : "-"}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
